import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, getDashboardRoute, API } from "../App";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Building2,
  DollarSign,
  Home,
  FileText,
  TrendingUp,
  Users,
  Settings,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  Briefcase,
  BarChart3,
  Shield,
  ClipboardList,
  Bell,
  Check,
  Clock,
  CheckCircle,
  Handshake,
  Heart,
  Archive,
  UserCheck,
  Store,
  User as UserIcon,
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import PrivacyStatementLink from "./PrivacyStatementLink";

// Navigation items by role
const getNavItems = (role) => {
  const baseItems = [
    { label: "Dashboard", href: getDashboardRoute(role), icon: Home },
  ];

  switch (role) {
    case "agent":
      return [
        ...baseItems,
        {
          label: "My Properties",
          href: "/dashboard/agent/properties",
          icon: FileText,
        },
        {
          label: "My Drafts",
          href: "/dashboard/agent/drafts",
          icon: Building2,
        },
      ];
    case "seller":
      return [
        ...baseItems,
        {
          label: "My Properties",
          href: "/dashboard/seller/properties",
          icon: FileText,
        },
        {
          label: "My Drafts",
          href: "/dashboard/seller/drafts",
          icon: Building2,
        },
      ];
    case "wholesaler":
      return [
        ...baseItems,
        {
          label: "Live Deals",
          href: "/dashboard/wholesaler/deals",
          icon: TrendingUp,
        },
        {
          label: "My Deals",
          href: "/dashboard/wholesaler/my-deals",
          icon: Briefcase,
        },
      ];
    case "investor":
      return [
        ...baseItems,
        {
          label: "Live Deals",
          href: "/dashboard/investor/deals",
          icon: TrendingUp,
        },
        {
          label: "My Deals",
          href: "/dashboard/investor/my-deals",
          icon: Briefcase,
        },
      ];
    case "admin":
      return [
        { label: "Dashboard", href: getDashboardRoute(role), icon: Home },
        { type: "group", label: "Deals" },
        {
          label: "Pending Review",
          href: "/dashboard/admin/deals?status=pending_review",
          icon: Clock,
          matchParam: "pending_review",
        },
        {
          label: "Live Deals",
          href: "/dashboard/admin/deals?status=live",
          icon: TrendingUp,
          matchParam: "live",
        },
        {
          label: "Interested Deals",
          href: "/dashboard/admin/deals?status=interested",
          icon: Heart,
          matchParam: "interested",
        },
        {
          label: "Under Contract",
          href: "/dashboard/admin/deals?status=under_contract",
          icon: Handshake,
          matchParam: "under_contract",
        },
        {
          label: "Deals Closed",
          href: "/dashboard/admin/deals?status=closed",
          icon: Archive,
          matchParam: "closed",
        },
        { type: "group", label: "Users" },
        {
          label: "Investors",
          href: "/dashboard/admin/users?role=investor",
          icon: DollarSign,
          matchParam: "investor",
        },
        {
          label: "Agents",
          href: "/dashboard/admin/users?role=agent",
          icon: Briefcase,
          matchParam: "agent",
        },
        {
          label: "Wholesalers",
          href: "/dashboard/admin/users?role=wholesaler",
          icon: Store,
          matchParam: "wholesaler",
        },
        {
          label: "Sellers",
          href: "/dashboard/admin/users?role=seller",
          icon: UserCheck,
          matchParam: "seller",
        },
        { type: "group", label: "Operations" },
        {
          label: "Documents",
          href: "/dashboard/admin/documents",
          icon: ClipboardList,
        },
        {
          label: "Platform Settings",
          href: "/dashboard/admin/platform-settings",
          icon: Shield,
        },
      ];
    default:
      return baseItems;
  }
};

const getRoleBadgeColor = (role) => {
  switch (role) {
    case "agent":
      return "bg-blue-600/10 text-blue-600";
    case "seller":
      return "bg-green-500/10 text-green-500";
    case "wholesaler":
      return "bg-purple-500/10 text-purple-500";
    case "investor":
      return "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400";
    case "admin":
      return "bg-blue-600/10 text-blue-600";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const DashboardLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const navItems = getNavItems(user?.role);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const headers = getAuthHeaders();
      const [notifResponse, countResponse] = await Promise.all([
        axios.get(`${API}/notifications?limit=10`, {
          withCredentials: true,
          headers,
        }),
        axios.get(`${API}/notifications/unread-count`, {
          withCredentials: true,
          headers,
        }),
      ]);
      setNotifications(notifResponse.data);
      setUnreadCount(countResponse.data.unread_count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/notifications/${notificationId}/read`,
        {},
        { withCredentials: true, headers },
      );
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/notifications/mark-all-read`,
        {},
        { withCredentials: true, headers },
      );
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar fixed lg:static inset-y-0 left-0 z-50 transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="sidebar-header">
          <div className="flex items-center gap-3">
            <img
              src="/logo.svg"
              alt="Cash Flow 4U"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="font-heading font-bold text-foreground">
                <span style={{ color: "#084F9B" }}>Cash</span>{" "}
                <span style={{ color: "#F8B105" }}>Flow</span>{" "}
                <span style={{ color: "#084F9B" }}>4U</span>
              </h1>
              <span className={`role-badge ${getRoleBadgeColor(user?.role)}`}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </span>
            </div>
          </div>
          <button
            className="lg:hidden absolute top-4 right-4 text-muted-foreground"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item, idx) => {
            if (item.type === "group") {
              return (
                <div key={`group-${idx}`} className="px-3 pt-5 pb-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                    {item.label}
                  </span>
                </div>
              );
            }
            const Icon = item.icon;
            const currentPath = location.pathname;
            const currentSearch = location.search;
            const itemPath = item.href.split("?")[0];
            const itemSearch = item.href.includes("?")
              ? "?" + item.href.split("?")[1]
              : "";
            const isActive = itemSearch
              ? currentPath === itemPath && currentSearch === itemSearch
              : currentPath === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`sidebar-item ${isActive ? "active" : ""}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <Link
            to="/dashboard/profile"
            className="sidebar-item"
            onClick={() => setSidebarOpen(false)}
            data-testid="nav-settings"
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </Link>
          <PrivacyStatementLink className="sidebar-item text-sm text-muted-foreground hover:text-foreground mt-1" />
        </div>
      </aside>

      {/* Main content */}
      <div className="main-content">
        {/* Top bar */}
        <header className="top-bar">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden text-muted-foreground"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h2 className="font-heading font-semibold text-lg text-foreground">
              {title || "Dashboard"}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative"
                  data-testid="notifications-button"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80" align="end">
                <div className="flex items-center justify-between p-3 border-b">
                  <h4 className="font-semibold">Notifications</h4>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                      <Check className="w-4 h-4 mr-1" />
                      Mark all read
                    </Button>
                  )}
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No notifications</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div
                        key={notif.notification_id}
                        className={`p-3 border-b hover:bg-muted/50 cursor-pointer ${!notif.read ? "bg-blue-500/5" : ""}`}
                        onClick={() => {
                          if (!notif.read) markAsRead(notif.notification_id);
                          // Navigate to relevant page based on notification type
                          if (notif.type === "deal_assigned") {
                            navigate("/dashboard/investor/my-deals");
                          }
                        }}
                      >
                        <div className="flex items-start gap-2">
                          {!notif.read && (
                            <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            {notif.title && (
                              <p className="font-medium text-sm">
                                {notif.title}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {notif.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(notif.sent_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                setTheme(resolvedTheme === "dark" ? "light" : "dark")
              }
              data-testid="dashboard-theme-toggle"
            >
              {resolvedTheme === "dark" ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-10 w-10 rounded-full"
                  data-testid="user-menu-button"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-blue-500 text-white">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="flex items-center gap-3 p-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-blue-500 text-white">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {user?.name}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/dashboard/profile")}
                  data-testid="profile-menu-item"
                >
                  <UserIcon className="w-4 h-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content area */}
        <main className="content-area">{children}</main>
      </div>
    </div>
  );
};

export default DashboardLayout;
